import {isUndefined} from 'lodash';

import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {CellsMessagesBoundary, makeMessageBoundaryBuilder} from '../../../../helpers/cellsHelpers';
import {sortMessages} from '../../../../helpers/models/messageHelpers';
import {CommentModel} from '../../../../models/api/commentModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {DataGroupModel} from '../../../../models/api/dataGroupModel';
import {MessageModel} from '../../../../models/api/messageModel';
import {ResourceMap} from '../../../../store/appTypes';
import {CellsMessagesListRange} from '../../../metadata/cellsLists/cellsList/cellsListState';
import {MaybeFetchedAt} from '../../../metadata/cellsLists/cellsListsHelpers';
import {InboxesTeammateMessagesParameters} from '../../inboxes/parameters/inboxesParameters';
import {findFirstSelectedId} from '../../inboxes/parameters/inboxesParametersHelpers';
import {
  updateInitialMoreStatusesAndLimits,
  updateMoreStatusAndLimit,
} from '../helpers/cellsPanelReducerHelpers';

export function fetchInitialCellsMessagesFromDataGroup(
  parameters: InboxesTeammateMessagesParameters,
  dataGroup: DataGroupModel,
  allConversations?: ResourceMap<ConversationModel>,
  allComments?: ResourceMap<CommentModel>,
): MaybeFetchedAt<CellsMessagesListRange> {
  const sortedMessages = extractAndSortMessages(parameters, dataGroup, allConversations, allComments);
  const boundaryBuilder = makeMessageBoundaryBuilder(parameters);
  const aroundMessageIndex = findAroundMessageIndex(parameters, sortedMessages);
  const {topBoundary, bottomBoundary} = updateInitialMoreStatusesAndLimits(
    boundaryBuilder,
    sortedMessages,
    aroundMessageIndex,
    dataGroup.limit || 0,
  );

  return {
    ids: sortedMessages.map((m) => m.uid),
    topBoundary,
    bottomBoundary,
    fetchedAt: dataGroup.fetchedAt,
  };
}

export function fetchMoreCellsMessagesFromDataGroup(
  parameters: InboxesTeammateMessagesParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsMessagesBoundary,
  dataGroup: DataGroupModel,
  allConversations: ResourceMap<ConversationModel>,
  allComments: ResourceMap<CommentModel>,
): MaybeFetchedAt<CellsMessagesListRange> {
  const sortedMessages = extractAndSortMessages(parameters, dataGroup, allConversations, allComments);
  const boundaryBuilder = makeMessageBoundaryBuilder(parameters);
  const statusAndLimit = updateMoreStatusAndLimit(
    boundaryBuilder,
    sortedMessages,
    direction,
    dataGroup.limit || 0,
  );

  if ('topBoundary' in statusAndLimit) {
    return {
      // Include the reference item, so we can have continuous ranges without needing to deal with
      // non-inclusive boundaries (i.e. open intervals).
      ids: [...sortedMessages.map((c) => c.uid), referenceBoundary.id],
      topBoundary: statusAndLimit.topBoundary,
      bottomBoundary: referenceBoundary,
      fetchedAt: dataGroup.fetchedAt,
    };
  }

  return {
    ids: [referenceBoundary.id, ...sortedMessages.map((c) => c.uid)],
    topBoundary: referenceBoundary,
    bottomBoundary: statusAndLimit.bottomBoundary,
    fetchedAt: dataGroup.fetchedAt,
  };
}

/** Read the messages from a message page and sort them. */
export function extractAndSortMessages(
  parameters: InboxesTeammateMessagesParameters,
  dataGroup: DataGroupModel,
  allConversations?: ResourceMap<ConversationModel>,
  allComments?: ResourceMap<CommentModel>,
) {
  const {messages} = dataGroup;
  if (!messages) {
    return [];
  }

  return sortMessages(parameters, messages, allConversations, allComments);
}

/** Find the index of the around message, if any. */
function findAroundMessageIndex(
  parameters: InboxesTeammateMessagesParameters,
  sortedMessages: ReadonlyArray<MessageModel>,
) {
  const aroundId = findFirstSelectedId(parameters.selection);
  if (isUndefined(aroundId)) {
    return undefined;
  }
  return sortedMessages.findIndex((c) => c.uid === aroundId);
}
